// The placement page (sheet, section 6, step 4; Spencer uses it once at cutover): each open or parked lead on its current
// step, first visit and design lane where known, the anchor confirmed, stale next-touch dates fixed, and the Schoeberl
// split. One row per lead, saved one at a time; days on a step count from the day it is placed.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCrumb } from '../../frame/Frame';
import { useRows } from '../../lib/api';
import { money, monthDay, today } from '../../lib/format';
import { conditionWords, placeLead, usePipeline, type Lead, type Pipeline, type Placement } from '../../lib/pipeline';
import { useSession } from '../../lib/session';
import { Btn, Empty, Head, Loading, Panel, Tag } from '../../ui/kit';
import { LeadValue } from './bits';
import { SplitDialog } from './Split';

export function PlacePage() {
  const p = usePipeline();
  const { can, canEdit } = useSession();
  useCrumb([{ label: 'Home', to: '/' }, { label: 'Pipeline', to: '/pipeline' }, { label: 'Placement' }]);
  if (p.loading) return <Loading what="Loading the leads to place" />;
  const leads = [...p.open, ...p.parked].sort((a, b) => Number(a.placed) - Number(b.placed) || (a.stage?.sort ?? 99) - (b.stage?.sort ?? 99) || a.name.localeCompare(b.name));
  const left = leads.filter((l) => !l.placed).length;
  return (
    <div className="stack">
      <Head title={`Place each lead · ${left} of ${leads.length} to go`} sub="Each open or parked lead: the step it is on today, first visit and design lane where known, its anchor confirmed, its next touch fixed. Save one at a time; days on a step count from the day you place it." />
      {!canEdit('screen.pipeline') && <Empty>Only a login that may change the pipeline places leads.</Empty>}
      {leads.length === 0 && <Empty>Nothing open or parked.</Empty>}
      {leads.map((l) => (
        <PlaceRow key={l.id} p={p} lead={l} prices={can('see_prices')} />
      ))}
    </div>
  );
}

function PlaceRow({ p, lead, prices }: { p: Pipeline; lead: Lead; prices: boolean }) {
  const { canEdit } = useSession();
  const editable = canEdit('screen.pipeline');
  const [stage, setStage] = useState<string>(lead.stage?.id ?? '');
  const [step, setStep] = useState<string>(lead.step?.id ?? '');
  const [firstVisit, setFirstVisit] = useState<string>(lead.opp.first_visit ?? '');
  const [lane, setLane] = useState<string>(lead.opp.design_lane ?? '');
  const [anchorType, setAnchorType] = useState<string>(lead.opp.anchor_type ?? '');
  const [anchorDate, setAnchorDate] = useState<string>(lead.opp.anchor_date ?? '');
  const [confirmed, setConfirmed] = useState<boolean>(!!lead.opp.anchor_confirmed_at);
  const [nextTouch, setNextTouch] = useState<string>(lead.nextTouch ?? '');
  const [busy, setBusy] = useState(false);
  const [splitting, setSplitting] = useState(false);
  const log = useRows('activity', { eq: { opportunity_id: lead.id }, order: ['-at'], enabled: splitting });
  const stageSteps = p.steps.filter((s) => s.stage_id === stage && !s.retired_at).sort((a, b) => a.sort - b.sort);
  const td = today();
  const late = nextTouch && nextTouch < td;
  const dirty = stage !== (lead.stage?.id ?? '') || step !== (lead.step?.id ?? '') || firstVisit !== (lead.opp.first_visit ?? '') || lane !== (lead.opp.design_lane ?? '') || anchorType !== (lead.opp.anchor_type ?? '') || anchorDate !== (lead.opp.anchor_date ?? '') || confirmed !== !!lead.opp.anchor_confirmed_at || nextTouch !== (lead.nextTouch ?? '');
  const save = async () => {
    setBusy(true);
    try {
      const pl: Placement = { stage: stage || null, step: step || null, first_visit: firstVisit || null, design_lane: lane || null, anchor_type: anchorType || null, anchor_date: anchorDate || null, anchor_confirmed: confirmed, next_touch: nextTouch || null };
      await placeLead(lead, pl);
    } finally {
      setBusy(false);
    }
  };
  const answers = (sets: string | null | undefined) => (sets === 'first_visit' ? p.answers.first_visit : sets === 'design_lane' ? p.answers.design_lane : null);
  return (
    <Panel className={`pplace${lead.placed ? ' placed' : ''}`}>
      <div className="pplace-head">
        <div>
          <Link to={`/opportunities/${lead.id}`} className="rowlink">
            <b>{lead.name}</b>
          </Link>
          <br />
          <small>
            {lead.opp.notion_stage ? `Notion: ${lead.opp.notion_stage}` : 'Made in ZeroGap'} · {lead.outcome === 'parked' ? 'parked' : 'open'} · {prices ? <LeadValue lead={lead} /> : null}
            {lead.lastTouch ? ` · last touch ${monthDay(lead.lastTouch.at)}` : ' · never touched'}
            {lead.jobs.length ? ` · ${lead.jobs.length} job${lead.jobs.length > 1 ? 's' : ''}` : ''}
          </small>
        </div>
        <span>{lead.placed && <Tag kind="on">placed {monthDay(lead.opp.current_step_since)}</Tag>}</span>
      </div>
      <div className="pplace-grid">
        <label className="field">
          <span>Stage</span>
          <select value={stage} disabled={!editable} onChange={(e) => { setStage(e.target.value); setStep(''); }}>
            <option value="">{lead.outcome === 'parked' ? 'No stage (parked)' : '—'}</option>
            {p.working.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field wide2">
          <span>Current step</span>
          <select value={step} disabled={!editable || !stage} onChange={(e) => setStep(e.target.value)}>
            <option value="">—</option>
            {stageSteps.map((s, i) => (
              <option key={s.id} value={s.id}>
                {i + 1}. {s.name}
                {s.applies_when ? ` · ${conditionWords(p, s)}` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>First visit</span>
          <select value={firstVisit} disabled={!editable} onChange={(e) => setFirstVisit(e.target.value)}>
            <option value="">Not decided</option>
            {p.answers.first_visit.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Design lane</span>
          <select value={lane} disabled={!editable} onChange={(e) => setLane(e.target.value)}>
            <option value="">Not set</option>
            {p.answers.design_lane.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Anchor</span>
          <select value={anchorType} disabled={!editable} onChange={(e) => setAnchorType(e.target.value)}>
            <option value="">—</option>
            {[...new Set(['Build complete', 'Deposit in', 'Retainer signed', ...(anchorType ? [anchorType] : [])])].map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Anchor date{anchorDate && anchorDate < td ? ' · past' : ''}</span>
          <input type="date" value={anchorDate} disabled={!editable} onChange={(e) => setAnchorDate(e.target.value)} />
        </label>
        <label className="field">
          <span>Anchor confirmed</span>
          <label className="check" style={{ minHeight: 32 }}>
            <input type="checkbox" checked={confirmed} disabled={!editable} onChange={(e) => setConfirmed(e.target.checked)} /> {confirmed ? 'yes' : 'not yet'}
          </label>
        </label>
        <label className="field">
          <span>Next touch{late ? ' · passed' : ''}</span>
          <input type="date" className={late ? 'late' : ''} value={nextTouch} disabled={!editable} onChange={(e) => setNextTouch(e.target.value)} />
        </label>
      </div>
      {step && answers(p.stepById(step)?.sets_answer as string | null) && (
        <small>
          This step records {p.stepById(step)?.sets_answer === 'first_visit' ? 'the first visit' : 'the design lane'} when it is done; leave that blank if it is still to be decided.
        </small>
      )}
      <div className="pctl" style={{ marginTop: 8 }}>
        <Btn primary disabled={!editable || busy || !dirty || (!!stage && !step)} onClick={() => void save()}>
          {lead.placed ? 'Save' : 'Place it'}
        </Btn>
        {editable && !busy && stage && !step && <small>pick the step first</small>}
        {editable && !busy && step && !dirty && lead.placed && <small>nothing changed</small>}
        {editable && lead.jobs.length > 1 && (
          <Btn onClick={() => setSplitting(true)} title="The rooms decide separately: split into two opportunities">
            Split
          </Btn>
        )}
        {lead.outcome === 'parked' && <small>Parked: re-approach on the next touch date; a stage is optional until then.</small>}
        {lead.value.kind === 'typed' && prices && <small>Typed value {money(lead.value.cents, { whole: true })} stands in until a job is priced.</small>}
      </div>
      {splitting && !log.loading && <SplitDialog p={p} lead={lead} log={log.rows} onClose={() => setSplitting(false)} onSplit={() => setSplitting(false)} />}
    </Panel>
  );
}
